import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import Entity from "../../core/entity/Entity";
import { PhysicsEvents } from "../../core/entity/PhysicsEvents";
import { clamp, lerp } from "../../core/util/MathUtil";
import { V, V2d } from "../../core/Vector";
import VectorSprite, { VectorSegment } from "../graphics/VectorSprite";
import { HitType, isHittable } from "../Hittable";
import { LevelEntity } from "../LevelEntity";

// Line circle effect constants
const DEFAULT_LIFETIME = 1.5; // seconds
const DEFAULT_RADIUS = 20; // radius of the circle
const LINE_SPACING = 3; // fixed Y distance between lines
const DEFAULT_LINE_THICKNESS = 2; // stroke width of lines
const DAMAGE = 10; // damage per tick.

const FADE_CURVE = (t: number): number => {
  return clamp(lerp(2, 0, t));
};

const RADIUS_CURVE = (t: number): number => {
  return t ** 0.5;
};

/**
 * Creates an explosion effect using horizontal line segments with damage capability
 * The lines suggest the shape of a circle without drawing an actual border
 * Can apply damage to entities within the expanding radius
 */
export default class ExplosionEffect extends LevelEntity implements Entity {
  private renderer: VectorSprite;
  body: NonNullable<Entity["body"]>;

  private lifetime: number = DEFAULT_LIFETIME;
  private maxRadius: number;
  private color: number;

  constructor(
    position: V2d,
    color: number = 0xffffff,
    radius: number = DEFAULT_RADIUS,
    lifetime: number = DEFAULT_LIFETIME,
  ) {
    super();

    this.color = color;
    this.maxRadius = radius;
    this.lifetime = lifetime;

    // Create line segment renderer with empty segments initially
    this.renderer = new VectorSprite([], "main");
    this.addChild(this.renderer);
    this.renderer.setPosition(position);

    this.body = new Body({
      mass: 0,
      type: Body.KINEMATIC,
      position: position.clone(),
    });

    this.body.addShape(
      new Circle({
        radius: 1,
        sensor: true,
        collisionGroup: collisionGroups("Explosion"),
        collisionMask: collisionGroups("Enemy", "Bomb"),
      }),
    );
  }

  get shape() {
    return this.body.shapes[0] as Circle;
  }

  async onAdd() {
    await this.wait(this.lifetime, (dt, t) => {
      const radius = RADIUS_CURVE(t) * this.maxRadius + 0.01; // Add small offset to avoid zero radius
      this.renderer.setAlpha(FADE_CURVE(t));

      this.shape.radius = radius;
      this.shape.updateArea();
      this.shape.updateBoundingRadius();
      this.body.updateAABB();
    });
    this.destroy();
  }

  onRender(): void {
    const radius = this.shape.radius;
    const segments: VectorSegment[] = [];

    // Generate line segments at fixed spacing intervals
    for (let y = -radius; y <= radius; y += LINE_SPACING) {
      // Calculate line half-width using circle equation: x = sqrt(r² - y²)
      const radiusSquared = radius ** 2;
      const ySquared = y ** 2;

      // Ensure we don't get negative values due to floating point precision
      if (radiusSquared >= ySquared) {
        const halfWidth = Math.sqrt(radiusSquared - ySquared);

        // Add the line segment
        segments.push({
          start: V(-halfWidth, y),
          end: V(halfWidth, y),
          color: this.color,
          thickness: DEFAULT_LINE_THICKNESS,
        });
      }
    }

    // Update renderer with new segments
    this.renderer.setSegments(segments);
  }

  onContacting({ other }: PhysicsEvents["contacting"]): void {
    if (isHittable(other)) {
      other.takeHit(DAMAGE, HitType.Explosion);
    }
  }
}
